import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { featuredTeamsApi } from '../api/featuredTeams';
import Seo from '../components/Seo';

const FeaturedTeamsPage = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const featuredTeamsEnabled = useSelector((state) => state.configs?.featuredTeamsEnabled);

  // The page only exists when the merchant has enabled Featured Teams
  useEffect(() => {
    if (!featuredTeamsEnabled) {
      navigate('/', { replace: true });
    }
  }, [featuredTeamsEnabled, navigate]);

  useEffect(() => {
    if (featuredTeamsEnabled) {
      loadTeams();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuredTeamsEnabled]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      // Merchant-defined teams only — no demo fallback
      const apiTeams = await featuredTeamsApi.getFeaturedTeams();
      setTeams(apiTeams);
    } catch (error) {
      console.error('Error loading teams:', error);
      setTeams([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTeamClick = (teamId) => {
    navigate(`/products?team=${teamId}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Seo title="Featured Teams" description="Shop collections from featured teams." />
      {/* Header Section */}
      <Box sx={{
        py: { xs: 2, md: 3 },
        background: 'linear-gradient(135deg, #f8fffe 0%, #e8f9f8 100%)',
        borderBottom: '1px solid #e0e0e0',
      }}>
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Box sx={{ mb: 2 }}>
            <Breadcrumbs>
              <Link
                component="button"
                variant="body2"
                onClick={() => navigate('/')}
                sx={{
                  color: 'var(--primary)',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Home
              </Link>
              <Typography variant="body2" sx={{ color: '#666' }}>
                Featured Teams
              </Typography>
            </Breadcrumbs>
          </Box>

          {/* Page Title */}
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                color: '#1A1A1A',
                mb: 1,
              }}
            >
              Wear Your Pride
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                fontSize: { xs: '0.875rem', md: '1rem' },
                maxWidth: 600,
              }}
            >
              Explore all featured teams and show your support with our exclusive collection of jerseys and merchandise.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Teams Grid */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Teams Count */}
            <Typography
              variant="subtitle1"
              sx={{
                color: '#666',
                mb: 4,
                fontSize: '0.95rem',
              }}
            >
              Showing {teams.length} teams
            </Typography>

            {/* Empty state — enabled but no teams created yet */}
            {teams.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
                  No teams to show yet
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Check back soon — in the meantime, browse the full catalogue.
                </Typography>
                <Button variant="contained" onClick={() => navigate('/products')} sx={{ backgroundColor: 'var(--button-primary)', '&:hover': { backgroundColor: 'var(--button-hover)' } }}>
                  Browse Products
                </Button>
              </Box>
            )}

            {/* Teams Grid */}
            <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
              {teams.map((team) => (
                <Grid item xs={6} sm={4} md={3} lg={2.4} key={team.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 3,
                      cursor: 'pointer',
                      background: team.gradient,
                      aspectRatio: '1',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                      transform: 'scale(1)',
                      '&:hover': {
                        transform: 'scale(1.05) translateY(-4px)',
                        boxShadow: '0 20px 30px rgba(0,0,0,0.12)',
                      },
                      p: 2,
                      textAlign: 'center',
                    }}
                    onClick={() => handleTeamClick(team.id)}
                  >
                    {/* Badge */}
                    {team.badge && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: team.badgeColor || 'var(--primary)',
                          color: '#fff',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.65rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {team.badge}
                      </Box>
                    )}

                    {/* Team Code */}
                    <Typography
                      sx={{
                        fontSize: { xs: '1.75rem', sm: '2rem' },
                        fontWeight: 800,
                        color: team.textColor || '#fff',
                        textShadow: 'rgba(0,0,0,0.1) 0 2px 4px',
                        mb: 1,
                      }}
                    >
                      {team.code}
                    </Typography>

                    {/* Team Name */}
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                        fontWeight: 600,
                        color: team.textColor || '#fff',
                        textShadow: 'rgba(0,0,0,0.1) 0 1px 2px',
                      }}
                    >
                      {team.name}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* No Teams Message */}
            {teams.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: '#999' }}>
                  No teams available at the moment.
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Back Button */}
        <Box sx={{ mt: 6, textAlign: 'center' }}>
          <Button
            startIcon={<ArrowBackIos sx={{ fontSize: '0.8rem' }} />}
            onClick={() => navigate('/')}
            sx={{
              color: 'var(--primary)',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'color-mix(in srgb, var(--primary) 5%, transparent)',
              },
            }}
          >
            Back to Home
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturedTeamsPage;
